export const COMPARISON_RULES = Object.freeze({ tolerancePoints: 5 })

export const COMPARISON_LEGEND = [
  { tone: "below", label: "Rot", symbol: "↓", description: `Mehr als ${COMPARISON_RULES.tolerancePoints} Prozentpunkte unter dem Durchschnitt.` },
  { tone: "near", label: "Orange", symbol: "≈", description: `Höchstens ${COMPARISON_RULES.tolerancePoints} Prozentpunkte unter oder über dem Durchschnitt.` },
  { tone: "above", label: "Grün", symbol: "↑", description: `Mehr als ${COMPARISON_RULES.tolerancePoints} Prozentpunkte über dem Durchschnitt.` },
]

export const CONTACT_SOURCES = [
  { key: "consultation", title: "Beratung", detailTitle: "Beratungstermine" },
  { key: "trial", title: "Probetraining", detailTitle: "Probetrainings" },
  { key: "guestbook", title: "Gästebuch", detailTitle: "Gästebucheinträge" },
]

export const OUTCOME_METRICS = [
  { key: "elevenDays", label: "11 Tage", detailLabel: "11 Tage aktiviert" },
  { key: "contracts", label: "Verträge", detailLabel: "Vertrag vorhanden", beforeContactKey: "contractsBeforeContact" },
  { key: "medicalHistory", label: "Anamnesen", detailLabel: "Anamnese vorhanden", beforeContactKey: "medicalHistoryBeforeContact" },
]

export function existingOutcomeHint(beforeContact) {
  if (!Number.isSafeInteger(beforeContact) || beforeContact < 0) return ""
  return `Davon ${beforeContact.toLocaleString("de-CH")} bereits vor dem ersten Kontakt im gewählten Zeitraum vorhanden.`
}

export function defaultComparisonSettings() {
  return { source: "consultation", search: "", onlyRed: false, sortKey: "outliers", sortDirection: "asc" }
}

const count = value => Number.isSafeInteger(value) && value >= 0 ? value : 0
const round = value => Math.round((value + Number.EPSILON) * 10) / 10
const quote = (amount, people) => people > 0 && amount <= people ? amount / people * 100 : null

export function compareOutcome(amount, people, reference) {
  const rawRate = quote(amount, people)
  const rate = rawRate === null ? null : round(rawRate)
  const result = { count: amount, rate, delta: null, tone: "neutral", symbol: "–", reason: "" }
  if (rawRate === null) return { ...result, reason: people ? "Keine gültige Personenquote" : "Keine zugeordneten Personen" }
  if (!reference.available) return { ...result, reason: reference.reason }
  // Use the displayed precision for the boundary so the color matches the visible figures.
  const delta = round(rate - reference.rate)
  if (delta < -COMPARISON_RULES.tolerancePoints) return { ...result, delta, tone: "below", symbol: "↓", reason: "Unter der Gesamtquote" }
  if (delta > COMPARISON_RULES.tolerancePoints) return { ...result, delta, tone: "above", symbol: "↑", reason: "Über der Gesamtquote" }
  return { ...result, delta, tone: "near", symbol: "≈", reason: "Im Bereich der Gesamtquote" }
}

export function buildStudioComparison(studios = [], selectedSource = "consultation") {
  const source = CONTACT_SOURCES.find(item => item.key === selectedSource) || CONTACT_SOURCES[0]
  const sourceTotals = Object.fromEntries(CONTACT_SOURCES.map(item => [item.key,
    studios.reduce((sum, studio) => sum + count(studio.rows?.find(row => row.key === item.key)?.total), 0),
  ]))
  const rows = studios.map(studio => {
    const original = studio.rows?.find(row => row.key === source.key) || {}
    const row = { key: source.key, title: source.detailTitle }
    for (const key of ["total", "linkedPeople", "unlinkedContacts", "hiddenContacts", "contractsBeforeContact", "medicalHistoryBeforeContact", ...OUTCOME_METRICS.map(item => item.key)]) row[key] = count(original[key])
    return { studioId: studio.studioId, name: studio.name, row }
  })
  const reference = {
    people: rows.reduce((sum, item) => sum + item.row.linkedPeople, 0),
    contacts: sourceTotals[source.key],
    unlinkedContacts: rows.reduce((sum, item) => sum + item.row.unlinkedContacts, 0),
    hiddenContacts: rows.reduce((sum, item) => sum + item.row.hiddenContacts, 0),
    metrics: {},
  }
  for (const metric of OUTCOME_METRICS) {
    const amount = rows.reduce((sum, item) => sum + item.row[metric.key], 0)
    const invalid = rows.some(item => item.row[metric.key] > item.row.linkedPeople)
    const rawRate = invalid ? null : quote(amount, reference.people)
    const rate = rawRate === null ? null : round(rawRate)
    let reason = ""
    if (rawRate === null) reason = "Keine gültige Gesamtquote"
    reference.metrics[metric.key] = { count: amount, rate, available: !reason, reason,
      beforeContact: metric.beforeContactKey ? rows.reduce((sum, item) => sum + item.row[metric.beforeContactKey], 0) : undefined }
  }
  return {
    source, sourceTotals, reference,
    rows: rows.map(item => {
      const metrics = Object.fromEntries(OUTCOME_METRICS.map(metric => [metric.key,
        { ...compareOutcome(item.row[metric.key], item.row.linkedPeople, reference.metrics[metric.key]),
          beforeContact: metric.beforeContactKey ? item.row[metric.beforeContactKey] : undefined },
      ]))
      const values = Object.values(metrics)
      const comparable = values.filter(metric => metric.tone !== "neutral")
      return { ...item, metrics, redCount: values.filter(metric => metric.tone === "below").length,
        comparable: comparable.length > 0, lowestDelta: comparable.length ? Math.min(...comparable.map(metric => metric.delta)) : null }
    }),
  }
}

export function selectStudioRows(comparison, settings = {}) {
  const search = String(settings.search || "").trim().toLocaleLowerCase("de-CH")
  const rows = comparison.rows.filter(item => (!search || item.name.toLocaleLowerCase("de-CH").includes(search)) && (!settings.onlyRed || item.redCount > 0))
  const byName = (a, b) => a.name.localeCompare(b.name, "de-CH", { numeric: true }) || String(a.studioId).localeCompare(String(b.studioId))
  const direction = settings.sortDirection === "desc" ? -1 : 1
  return rows.slice().sort((a, b) => {
    const firstEmpty = a.row.linkedPeople === 0, secondEmpty = b.row.linkedPeople === 0
    if (firstEmpty !== secondEmpty) return firstEmpty ? 1 : -1
    if (settings.sortKey === "name") return byName(a, b) * direction
    if (settings.sortKey === "total") return (a.row.total - b.row.total) * direction || byName(a, b)
    if (OUTCOME_METRICS.some(metric => metric.key === settings.sortKey)) {
      const first = a.metrics[settings.sortKey], second = b.metrics[settings.sortKey]
      const firstNeutral = first.tone === "neutral", secondNeutral = second.tone === "neutral"
      if (firstNeutral !== secondNeutral) return firstNeutral ? 1 : -1
      if (first.rate === null || second.rate === null) return first.rate === second.rate ? byName(a, b) : first.rate === null ? 1 : -1
      return (first.rate - second.rate) * direction || byName(a, b)
    }
    if (a.comparable !== b.comparable) return a.comparable ? -1 : 1
    return b.redCount - a.redCount || (a.lowestDelta ?? 0) - (b.lowestDelta ?? 0) || byName(a, b)
  })
}
